import { useState } from "react";
import { useForm } from "react-hook-form";
import Form from "../../../components/Form";
import DynamicFormRenderer from "../../../components/DynamicFormRenderer";
import { visaApplicationFormConfig } from "./formConfig";
import Api from "../../../Api";
import VisaLogo from "../../../assets/servece_visa_logo.png";

const templateStyles = {
  formHeader: {
    fontSize: "1.8em",
    margin: "10px 0 20px 0",
    color: "#0052CC",
    fontWeight: "600"
  },
  introBox: {
    background: "#E3F2FD",
    border: "1px solid #90CAF9",
    borderRadius: "10px",
    padding: "16px 18px",
    marginBottom: "22px",
    lineHeight: "1.6",
    textAlign: "start",
    color: "#1565C0"
  },
  sectionTitle: {
    marginTop: "26px",
    marginBottom: "10px",
    fontSize: "1.15em",
    color: "#0052CC !important",
    fontWeight: "600"
  },
  formLabel: {
    fontWeight: "600",
    marginBottom: "8px",
    display: "block",
    color: "#0052CC",
  },
  formControl: {
    width: "100%",
    borderRadius: "8px",
    padding: "12px 16px",
    border: "1px solid #B0BEC5",
    boxSizing: "border-box",
    background: "#fff",
  },
  button: {
    padding: "12px 32px",
    fontSize: "1rem",
    borderRadius: "8px",
    border: "none",
    background: "#0052CC",
    color: "#fff",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.3s ease"
  },
};

const VisaApplication = (props) => {
  const { metadata: { brandColors } } = visaApplicationFormConfig;
  const [loading, setLoading] = useState(false);
  const [showModalSucces, setShowModalSucces] = useState(false);
  const [showModalError, setShowModalError] = useState(false);

  // Initialize react-hook-form
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    unregister,
    reset,
    control
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {},
    shouldFocusError: true,
    criteriaMode: 'all'
  });

  const submitForm = async (data) => {
    try {
      setLoading(true);

      // Build linkedContainer hierarchy map
      const linkedContainerMap = {}; // displayId -> { defDossierId, parentDisplayId, data }

      // Helper to get prefix from field name (e.g., "133242_133250" -> "133242")
      const getDisplayId = (fieldName) => {
        const match = String(fieldName).match(/^(\d+)_/);
        return match ? match[1] : null;
      };

      // Build the hierarchy structure
      for (const section of visaApplicationFormConfig.sections) {
        if (section.linkedContainer) {
          linkedContainerMap[section.linkedContainer.displayId] = {
            defDossierId: section.linkedContainer.defdossierId,
            parentDisplayId: null,
            data: {}
          };
        }
        if (section.fields) {
          for (const field of section.fields) {
            if (field.type === 'section' && field.linkedContainer) {
              linkedContainerMap[field.linkedContainer.displayId] = {
                defDossierId: field.linkedContainer.defdossierId,
                parentDisplayId: section.linkedContainer?.displayId || null,
                data: {}
              };
            }
          }
        }
      }

      // Separate form data by displayId
      const parentFormData = {};
      for (const [key, value] of Object.entries(data)) {
        const displayId = getDisplayId(key);
        if (displayId && linkedContainerMap[displayId]) {
          const fieldName = String(key).replace(/^\d+_/, '');
          linkedContainerMap[displayId].data[fieldName] = value;
        } else {
          parentFormData[key] = value;
        }
      }

      // Submit in hierarchical order (deepest first)
      const linkedContainerIds = {};

      // Helper to get submission order (children before parents)
      const getSubmissionOrder = () => {
        const order = [];
        const visited = new Set();

        const visit = (displayId) => {
          if (visited.has(displayId)) return;
          visited.add(displayId);

          // Find children of this displayId and visit them first
          for (const [childId, childConfig] of Object.entries(linkedContainerMap)) {
            if (childConfig.parentDisplayId === displayId) {
              visit(childId);
            }
          }

          order.push(displayId);
        };

        // Start from all root containers (parentDisplayId is null)
        for (const [displayId, config] of Object.entries(linkedContainerMap)) {
          if (config.parentDisplayId === null) {
            visit(displayId);
          }
        }

        return order;
      };

      const submissionOrder = getSubmissionOrder();

      // Submit in order: deepest nested first (6205) -> parent (6204) -> root (6203)
      for (const displayId of submissionOrder) {
        const containerConfig = linkedContainerMap[displayId];
        const containerData = containerConfig.data;

        // Include parent linkedContainer ID if available
        const submitData = { ...containerData };
        if (containerConfig.parentDisplayId && linkedContainerIds[containerConfig.parentDisplayId]) {
          submitData[containerConfig.parentDisplayId] = linkedContainerIds[containerConfig.parentDisplayId];
        }

        // Include child linkedContainer IDs
        for (const [childId, childConfig] of Object.entries(linkedContainerMap)) {
          if (childConfig.parentDisplayId === displayId && linkedContainerIds[childId]) {
            submitData[childId] = linkedContainerIds[childId];
          }
        }

        // Check if this container has any children that were submitted
        const hasChildren = Object.entries(linkedContainerMap).some(([childId, childConfig]) =>
          childConfig.parentDisplayId === displayId && linkedContainerIds[childId]
        );

        // Only submit if there's data OR if this container has children that need to be linked
        const hasDirectData = Object.keys(containerData).length > 0;
        const hasChildrenWithIds = hasChildren;

        if (!hasDirectData && !hasChildrenWithIds) continue;

        try {
          const response = await Api.dossiers.Create({
            defDossier: containerConfig.defDossierId,
            ...submitData
          },
            visaApplicationFormConfig.metadata.apiUsername,
            visaApplicationFormConfig.metadata.apiKey);

          if (response && response.id) {
            linkedContainerIds[displayId] = response.id;
          }
        } catch (error) {
          console.error(`Error submitting linkedContainer ${displayId}:`, error);
          throw error;
        }
      }

      // Submit parent form with only root-level linkedContainer IDs
      const rootContainerIds = Object.fromEntries(
        Object.entries(linkedContainerIds).filter(
          ([displayId]) => linkedContainerMap[displayId]?.parentDisplayId === null
        )
      );

      const finalData = {
        defDossier: visaApplicationFormConfig.metadata.defDossier,
        ...parentFormData,
        ...rootContainerIds
      };

      await Api.dossiers.Create(
        finalData,
        visaApplicationFormConfig.metadata.apiUsername,
        visaApplicationFormConfig.metadata.apiKey
      );

      setLoading(false);
      setShowModalSucces(true);
    } catch (error) {
      console.error('Form submission error:', error);
      setLoading(false);
      setShowModalError(true);
    }
  };

  return (
    <>
      <style jsx>{`
        .form-input:focus {
          outline: none;
          border-color: ${brandColors.primary};
          box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.1);
          transform: translateY(-1px);
        }

        .form-textarea:focus {
          outline: none;
          border-color: ${brandColors.primary};
          box-shadow: 0 0 0 3px rgba(0, 82, 204, 0.1);
          transform: translateY(-1px);
        }

        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-slide-up {
          animation: slideUp 0.2s ease-out;
        }

        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .section-enter {
          animation: sectionEnter 0.4s ease-out;
        }

        @keyframes sectionEnter {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .progress-bar {
          transition: width 0.3s ease-out;
        }

        .form-field-enter {
          animation: fieldEnter 0.3s ease-out;
        }

        @keyframes fieldEnter {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .tooltip {
          position: relative;
        }

        .tooltip::before,
        .tooltip::after {
          position: absolute;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease-in-out;
          z-index: 1000;
        }

        .tooltip::before {
          content: attr(data-tooltip);
          background: #1f2937;
          color: white;
          font-size: 12px;
          line-height: 1.4;
          padding: 8px 12px;
          border-radius: 6px;
          white-space: nowrap;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(-4px);
        }

        .tooltip::after {
          content: "";
          width: 0;
          height: 0;
          border-left: 4px solid transparent;
          border-right: 4px solid transparent;
          border-top: 4px solid #1f2937;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
        }

        .tooltip:hover::before,
        .tooltip:hover::after {
          opacity: 1;
        }
      `}</style>

      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-gray-900 bg-opacity-75"></div>
          <div className="relative">
            <div className="h-16 w-16 rounded-full border-4 animate-spin" style={{ borderColor: brandColors.border, borderTopColor: brandColors.primary }}></div>
            <div className="absolute inset-0 h-16 w-16 rounded-full border-4 border-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s', borderTopColor: brandColors.primaryLight }}></div>
          </div>
        </div>
      )}

      {showModalError && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowModalError(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Erreur</h3>
                  <button
                    onClick={() => setShowModalError(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900 mb-6 text-center">
                  Une erreur s'est produite lors de la soumission du formulaire.
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => setShowModalError(false)}
                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-200 transition-all duration-200"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModalSucces && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={() => setShowModalSucces(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Demande Soumise</h3>
                  <button
                    onClick={() => setShowModalSucces(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4" style={{ backgroundColor: `${brandColors.primary}20` }}>
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: brandColors.primary }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <p className="text-lg font-medium text-gray-900 mb-6 text-center">
                  Votre demande de visa a été soumise avec succès.
                </p>
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      setShowModalSucces(false);
                      reset();
                    }}
                    className="px-6 py-3 text-white font-semibold rounded-lg focus:outline-none transition-all duration-200 inline-block text-center"
                    style={{
                      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 100%)`
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = `linear-gradient(135deg, ${brandColors.primaryDark} 0%, ${brandColors.primary} 100%)`;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 100%)`;
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50" role="main" aria-labelledby="form-title">
        {/* Enhanced Header/Cover Section with Flag Colors */}
        <div
          className="relative overflow-hidden px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24"
          style={{
            background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 100%)`
          }}
        >
          {/* Decorative background elements */}
          <div
            className="absolute top-0 right-0 -mr-40 -mt-40 w-80 h-80 rounded-full opacity-10 blur-3xl"
            style={{ backgroundColor: brandColors.success }}
          ></div>
          <div
            className="absolute bottom-0 left-0 -ml-40 -mb-40 w-80 h-80 rounded-full opacity-15 blur-3xl"
            style={{ backgroundColor: brandColors.accent }}
          ></div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* Header Content */}
            <div className="text-center">
              {/* Logo */}
              <div className="mb-6 flex justify-center">
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-white opacity-5 rounded-2xl blur-xl"></div>
                  <img
                    src={VisaLogo}
                    alt="Visa Logo"
                    className="h-24 sm:h-32 lg:h-40 mx-auto relative drop-shadow-lg"
                  />
                </div>
              </div>

              <h1
                id="form-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-2 tracking-tight"
                style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
              >
                Demande de E-Visa
              </h1>

              <div className="h-1 w-20 mx-auto mb-6 rounded-full" style={{ backgroundColor: brandColors.success }}></div>

              <p
                className="text-lg sm:text-xl text-white text-opacity-90 max-w-2xl mx-auto mb-8"
                style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
              >
                Complétez votre demande de visa en ligne de manière sécurisée en quelques minutes. Rapide, simple et sécurisé.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content Section */}
        <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Important Information Box */}
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div
              className="px-6 py-1 bg-gradient-to-r to-transparent border-b"
              style={{
                background: `linear-gradient(90deg, ${brandColors.primary}10 0%, transparent 100%)`,
                borderColor: `${brandColors.primary}20`
              }}
            ></div>
            <div className="px-6 sm:px-8 py-8">
              <div className="flex items-start gap-4">
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-1"
                  style={{ backgroundColor: `${brandColors.primary}15` }}
                >
                  <svg className="w-6 h-6" style={{ color: brandColors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ color: brandColors.primary }}>
                    Informations Importantes
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex gap-3">
                      <span className="font-bold mt-0.5" style={{ color: brandColors.success }}>✓</span>
                      <span><strong>La précision est essentielle :</strong> Assurez-vous que toutes les informations fournies sont exactes et à jour.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold mt-0.5" style={{ color: brandColors.success }}>✓</span>
                      <span><strong>Délai de traitement :</strong> Le processus de demande prend généralement 5 à 10 jours ouvrables.</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="font-bold mt-0.5" style={{ color: brandColors.success }}>✓</span>
                      <span><strong>Champs obligatoires :</strong> Remplissez tous les champs marqués avec <span style={{ color: brandColors.primary }} className="font-bold">*</span> avant de soumettre.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full">
            <div className="px-6 sm:px-8 lg:px-12 py-6" style={{ background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 100%)` }}>
              <h2
                className="text-2xl sm:text-3xl font-semibold text-white"
                style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
              >
                📋 Détails de la Demande
              </h2>
              <p className="text-white text-opacity-90 text-sm sm:text-base mt-2">Remplissez toutes les informations requises ci-dessous</p>
            </div>

            <Form
              className="p-4 sm:p-6 lg:p-8 animate-fade-in w-full"
              {...props}
              register={register}
              handleSubmit={handleSubmit}
              setValue={setValue}
              unregister={unregister}
              formState={{ errors }}
              onSubmit={submitForm}
              defDossier={visaApplicationFormConfig.metadata.defDossier}
              reset={reset}
              apiUsername={visaApplicationFormConfig.metadata.apiUsername}
              apiKey={visaApplicationFormConfig.metadata.apiKey}
              skipLinkedContainers={true}
            >
              {/* Dynamic Form Renderer */}
              <DynamicFormRenderer
                config={visaApplicationFormConfig}
                register={register}
                errors={errors}
                setValue={setValue}
                unregister={unregister}
                control={control}
                watch={watch}
                className="animate-fade-in"
              />
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-start text-xs sm:text-sm text-gray-600">
                  <span className="font-semibold">Avis de Confidentialité :</span> En soumettant ce formulaire, vous reconnaissez que les informations fournies sont vraies et exactes et consentez à notre politique de confidentialité.
                </p>
              </div>
            </Form>
          </div>
        </div>

        {/* Footer Section */}
        <div
          className="mt-12 text-white py-8 px-4 sm:px-6 lg:px-8 backdrop-blur-sm"
          style={{
            background: `linear-gradient(135deg, ${brandColors.primaryDark} 0%, ${brandColors.primary} 100%)`
          }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center justify-center md:justify-start gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Besoin d'Aide ?
                </h4>
                <p className="text-sm text-white text-opacity-80">Contactez notre équipe d'assistance disponible 24/7 pour vous aider avec votre demande.</p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center justify-center md:justify-start gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Sécurité
                </h4>
                <p className="text-sm text-white text-opacity-80">Vos données sont chiffrées et protégées selon les protocoles de sécurité standards du secteur.</p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-2 flex items-center justify-center md:justify-start gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                  </svg>
                  Délai de Traitement
                </h4>
                <p className="text-sm text-white text-opacity-80">La plupart des demandes sont traitées dans les 5 à 10 jours ouvrables. Vérifiez le statut par e-mail.</p>
              </div>
            </div>
            <div className="border-t mt-8 pt-8 text-center text-sm text-white text-opacity-70" style={{ borderColor: `${brandColors.accent}40` }}>
              <p>© 2024 Portail de Demande de E-Visa. Tous droits réservés.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VisaApplication;
